import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpService } from '../../utility/http-service';

import { Subscription, firstValueFrom } from 'rxjs';
import { LoginService } from '../../../services/login-service';
import { ClientConfiguration } from '../../models/client-configuration.model';

// Replace this with your real user context source
// e.g. UserContextService / AuthService / whatever you already use in WellChron.
export interface LoggedInUserInfo {
  userId: string;
  displayName: string;
  email: string;
  companyName?: string;
  clientId?: string;
  roles?: string[];
}

export interface UserRequestPayload {
  // user context
  userId: string;
  displayName: string;
  email: string;
  companyName?: string | null;
  clientId?: string | null;
  roles: string[];

  // ticket info
  category: string;
  severity: string;
  subject: string;
  description: string;

  // optional extras
  includeDiagnostics: boolean;
  stepsToReproduce?: string | null;
  expectedResult?: string | null;
  actualResult?: string | null;
  pageUrl?: string | null;
  module?: string | null;

  submittedAtLocal: string; // ISO string
}


// Example stub service shape (swap with your own)
// import { UserContextService } from '...';

type SupportSeverity = 'Low' | 'Medium' | 'High' | 'Urgent';
type SupportCategory =
  | 'Bug'
  | 'Data Issue'
  | 'Performance'
  | 'Feature Request'
  | 'Access/Login'
  | 'Other';

@Component({
  selector: 'app-dev-support',
  templateUrl: './dev-support.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule
  ],
  styleUrls: ['./dev-support.component.css'] // optional; remove if you don’t want scss
})
export class DevSupportComponent implements OnInit, OnDestroy {
  form!: ReturnType<FormBuilder['group']>;
  constructor(
    private fb: FormBuilder,
    private http: HttpService,
    public loginService: LoginService
  ) {
    // ✅ fb is available here
    this.form = this.fb.group({
      category: ['Bug' as SupportCategory, Validators.required],
      severity: ['Medium' as SupportSeverity, Validators.required],
      subject: ['', [Validators.required, Validators.maxLength(120)]],
      description: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(4000)]],
      includeDiagnostics: [true],
      stepsToReproduce: [''],
      expectedResult: [''],
      actualResult: [''],
      pageUrl: [''],
      module: ['']
    });
  }
  private subs = new Subscription();

  // If you already have user info somewhere else, just assign it in ngOnInit.
  userInfo: LoggedInUserInfo | null = null;
  userClient: ClientConfiguration | null = null;  
  severities: SupportSeverity[] = ['Low', 'Medium', 'High', 'Urgent'];
  categories: SupportCategory[] = [
    'Bug',
    'Data Issue',
    'Performance',
    'Feature Request',
    'Access/Login',
    'Other'
  ];

  submitting = false;
  submitSuccess = false;
  errorMessage: string | null = null;

  // attachments
  attachments: File[] = [];
  maxFileSizeMb = 15;
  maxFiles = 5;
  allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.gif'];

  async ngOnInit(): Promise<void> {

    const u = this.loginService.userOptional;
    const name = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
    this.userInfo = {
      userId: 'current-user-id',
      displayName: name,
      email: u!.userName,
    };
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get f() {
    return this.form.controls;
  }

  onFilesSelected(event: Event) {
    this.errorMessage = null;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const incoming = Array.from(input.files);

    // enforce max count
    const remainingSlots = this.maxFiles - this.attachments.length;
    const toAdd = incoming.slice(0, Math.max(0, remainingSlots));

    const valid: File[] = [];
    for (const file of toAdd) {
      const extension = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
      if (!this.allowedExtensions.includes(extension)) {
        this.errorMessage = `File "${file.name}" isn't an allowed type. Only PDF and image files (PDF, PNG, JPG, GIF) are accepted.`;
        continue;
      }

      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > this.maxFileSizeMb) {
        this.errorMessage = `File "${file.name}" is too large (${sizeMb.toFixed(
          1
        )}MB). Max is ${this.maxFileSizeMb}MB.`;
        continue;
      }
      valid.push(file);
    }

    this.attachments = [...this.attachments, ...valid];

    // reset input so selecting the same file again works
    input.value = '';
  }

  removeAttachment(idx: number) {
    this.attachments = this.attachments.filter((_, i) => i !== idx);
  }

  buildPayload(): UserRequestPayload {
    if (!this.userInfo) {
      throw new Error('User info not loaded.');
    }

    const v = this.form.value;

    return {
      userId: this.userInfo.userId,
      displayName: this.userInfo.displayName,
      email: this.userInfo!.email,
      companyName: this.userInfo.companyName ?? null,
      clientId: this.userInfo.clientId ?? null,
      roles: this.userInfo.roles ?? [],

      category: v.category!,
      severity: v.severity!,
      subject: (v.subject ?? '').trim(),
      description: (v.description ?? '').trim(),

      includeDiagnostics: !!v.includeDiagnostics,
      stepsToReproduce: (v.stepsToReproduce ?? '').trim() || null,
      expectedResult: (v.expectedResult ?? '').trim() || null,
      actualResult: (v.actualResult ?? '').trim() || null,
      pageUrl: (v.pageUrl ?? '').trim() || null,
      module: (v.module ?? '').trim() || null,

      submittedAtLocal: new Date().toISOString()
    };
  }

  private buildFormData(payload: UserRequestPayload): FormData {
    const fd = new FormData();

    fd.append('payload', JSON.stringify(payload));

    for (const file of this.attachments) {
      fd.append('files', file, file.name);
    }

    return fd;
  }

  async submit(): Promise<void> {
    this.submitSuccess = false;
    this.errorMessage = null;

    if (!this.userInfo) {
      this.errorMessage = 'Unable to load your user profile. Please refresh.';
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.submitting = true;

    try {
      const payload = this.buildPayload();
      const formData = this.buildFormData(payload);
      this.http.post(
        'DevSupport/ProcessUserRequest',
        (res: any) => {
          this.submitSuccess = true;

          this.form.reset({
            category: 'Bug',
            severity: 'Medium',
            pageUrl: window?.location?.href ?? '',
            module: ''
          });

          this.attachments = [];
          this.submitting = false;
        },
        (err: any) => {
          console.error('Dev support submission failed', err);
          this.errorMessage = 'Failed to submit support request.';
          this.submitting = false;
        },
        formData
      );
    } catch (e) {
      this.errorMessage = 'Unexpected error submitting request.';
      this.submitting = false;
    }
  }

}
